import {z} from 'zod';
import {interpolateColors} from 'remotion';
import type {AiSceneResult} from '../../vendor/src/shared/apis/editor/post-ai-scene-generate';
import type {EditorState} from '../../vendor/src/features/remotion-editor/editor/state/types';
import {ANIMATION_META} from '../../vendor/src/features/remotion-editor/editor/items/animation/animation-meta';
import {addAiScene} from '../../vendor/src/features/remotion-editor/editor/state/actions/add-ai-scene';
import {hasPendingTimelineEdit} from './edit-ownership';
import {createNativeDocumentFile,readNativeDocumentFile} from './native-document-file';
import {nativeAiSceneFrameBudget} from '../../protocol/ai-scene-request';

const finite=z.number().finite();
const text=z.string().min(1).max(10000);
// Native solid uses backgroundColor, not arbitrary CSS background images.
const paint=z.string().min(1).max(128).refine(value=>{
 try{interpolateColors(0,[0,1],[value,value]);return true;}catch{return false;}
});
const textItem=z.object({
 text,top:finite.min(-4).max(4),left:finite.min(-4).max(4),width:finite.positive().max(4),height:finite.nonnegative().max(4),
 opacity:finite.min(0).max(1),fontSize:finite.positive().max(2048),color:paint,
 align:z.enum(['left','center','right']),fontFamily:z.string().trim().min(1).max(128).nullable(),
 animationType:z.string().refine(value=>Object.hasOwn(ANIMATION_META,value)).nullable(),
 fadeInSeconds:finite.nonnegative(),fadeOutSeconds:finite.nonnegative(),delaySeconds:finite.nonnegative(),
}).strict();
const scene=z.object({
 title:z.string().max(1000),totalDurationSeconds:finite.nonnegative(),
 segments:z.array(z.object({
  startSeconds:finite.nonnegative().nullable(),durationSeconds:finite.positive(),
  background:z.object({color:paint,transparent:z.boolean().optional()}).strict(),
  textItems:z.array(textItem).max(100),transitionType:z.string().max(64).nullable(),transitionDirection:z.string().max(64).nullable(),
 }).strict()).min(1).max(100),
}).strict();

/** Parse the company's native-scene contract, resolving all times before any editor mutation. */
export function readAiSceneResult(raw:unknown,options:{fps:number;width:number;maxDurationSeconds:number}){
 const {fps,width,maxDurationSeconds}=options;
 if(!Number.isFinite(width)||width<=0)throw Error('씬 생성 기준을 확인해 주세요.');
 const limit=nativeAiSceneFrameBudget(maxDurationSeconds,fps);
 const parsed=scene.safeParse(raw);if(!parsed.success)throw Error('씬 생성 응답의 형식이 올바르지 않습니다. 기존 편집은 유지됩니다.');
 if(parsed.data.segments.reduce((sum,segment)=>sum+segment.textItems.length,0)>500)throw Error('생성 요소가 너무 많습니다. 씬을 나누어 요청해 주세요.');
 if(parsed.data.segments.reduce((sum,segment)=>sum+segment.textItems.reduce((size,item)=>size+item.text.length,0),0)>100000)throw Error('생성된 글자가 너무 많습니다. 씬을 나누어 요청해 주세요.');
 let cursor=0,extent=0;
 const segments=parsed.data.segments.map(segment=>{
  const from=segment.startSeconds===null?cursor:Math.round(segment.startSeconds*fps),duration=Math.round(segment.durationSeconds*fps);
  if(!Number.isSafeInteger(from)||!Number.isSafeInteger(duration)||duration<1||from+duration>limit)throw Error('생성된 씬의 시간이 요청 범위를 벗어났습니다.');
  for(const item of segment.textItems){
   const delay=Math.round(item.delaySeconds*fps);
   if(!Number.isSafeInteger(delay)||delay>=duration||Math.round(item.width*width)<1||item.fadeInSeconds>maxDurationSeconds||item.fadeOutSeconds>maxDurationSeconds)throw Error('생성된 텍스트의 시간 또는 크기를 확인해 주세요.');
  }
  // Upstream's cursor adds durations only. Explicit starts for every segment preserve later gaps.
  cursor=from+duration;extent=Math.max(extent,cursor);
  return {...segment,startSeconds:from/fps,durationSeconds:duration/fps};
 });
 const value:AiSceneResult={...parsed.data,segments,totalDurationSeconds:extent/fps};
 return {value,notices:[
  ...(segments.some(segment=>segment.transitionType||segment.transitionDirection)?['회사 생성 액션은 세그먼트 전환을 삽입하지 않습니다. 전환 도구에서 따로 설정해 주세요.']:[]),
  ...(segments.some(segment=>segment.background.transparent&&!segment.textItems.length)?['내용이 없는 투명 구간은 새 항목을 만들지 않습니다.']:[]),
 ]};
}

export function prepareAiScenePlan(state:EditorState,raw:unknown,maxDurationSeconds=15){
 if(!state.initialized||hasPendingTimelineEdit(state))throw Error('편집 준비와 이동·자르기를 마친 뒤 씬을 추가해 주세요.');
 const before=state.undoableState;
 const parsed=readAiSceneResult(raw,{fps:before.fps,width:before.compositionWidth,maxDurationSeconds});
 const candidate=addAiScene({state,sceneResult:parsed.value});
 const createdIds=Object.keys(candidate.undoableState.items).filter(id=>!Object.hasOwn(before.items,id));
 if(Object.entries(before.items).some(([id,item])=>candidate.undoableState.items[id]!==item)||new Set(candidate.undoableState.tracks.map(track=>track.id)).size!==candidate.undoableState.tracks.length)throw Error('새 씬의 항목 번호가 기존 편집과 충돌했습니다.');
 if(createdIds.length){
  // Validation envelope only; this function neither saves nor changes the host revision.
  const file=createNativeDocumentFile(candidate,{savedAt:new Date().toISOString(),projectType:null});
  if(readNativeDocumentFile(file).status!=='ready')throw Error('생성된 씬을 정밀 편집 문서로 확인하지 못했습니다.');
 }
 return {before,fingerprint:JSON.stringify(before),after:candidate.undoableState,selection:candidate.selectedItems===state.selectedItems?null:candidate.selectedItems,createdIds,notices:parsed.notices};
}
export type AiScenePlan=ReturnType<typeof prepareAiScenePlan>;
/** Safe inside a functional updater: stale, pending and empty plans return the current state. */
export function applyAiScenePlan(state:EditorState,plan:AiScenePlan):EditorState{
 if(state.undoableState!==plan.before||hasPendingTimelineEdit(state)||!plan.createdIds.length)return state;
 try{if(JSON.stringify(state.undoableState)!==plan.fingerprint)return state;}catch{return state;}
 return {...state,undoableState:plan.after,selectedItems:plan.selection??state.selectedItems};
}
